const TURKISH_MAP = {
  ç: 'c',
  ğ: 'g',
  ı: 'i',
  ö: 'o',
  ş: 's',
  ü: 'u',
  Ç: 'c',
  Ğ: 'g',
  İ: 'i',
  Ö: 'o',
  Ş: 's',
  Ü: 'u',
};

/**
 * Automatically builds a table of contents from the H2 headings of a blog post,
 * enables smooth-scroll navigation, tracks the active section while scrolling,
 * and injects an ItemList structured data block for rich results.
 */
class BlogPostToc extends HTMLElement {
  #observer = null;
  #resizeListener = null;

  connectedCallback() {
    this.content = this.querySelector('[data-toc-content]');
    this.navs = Array.from(this.querySelectorAll('[data-toc-nav]'));
    this.containers = Array.from(this.querySelectorAll('[data-toc-desktop], [data-toc-mobile]'));

    if (!this.content || this.navs.length === 0) return;

    this.#build();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    if (this.#resizeListener) {
      window.removeEventListener('resize', this.#resizeListener);
    }
  }

  #isDesktop() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  #syncContainers() {
    const isDesktop = this.#isDesktop();

    for (const container of this.containers) {
      const showDesktop = container.matches('[data-toc-desktop]');
      container.hidden = isDesktop ? !showDesktop : showDesktop;
    }
  }

  #slugify(text) {
    const normalized = text
      .trim()
      .replace(/[çğıöşüÇĞİÖŞÜ]/g, (char) => TURKISH_MAP[char] || char);

    return (
      normalized
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '') || 'bolum'
    );
  }

  #build() {
    const headings = Array.from(this.content.querySelectorAll('h2'));

    if (headings.length === 0) {
      this.setAttribute('data-toc-empty', '');
      return;
    }

    const usedIds = new Set();
    const items = [];

    for (const heading of headings) {
      const text = (heading.textContent || '').trim();
      if (!text) continue;

      let id = heading.id || this.#slugify(text);
      let candidate = id;
      let counter = 2;
      while (usedIds.has(candidate)) {
        candidate = `${id}-${counter}`;
        counter += 1;
      }
      id = candidate;
      usedIds.add(id);
      heading.id = id;

      items.push({ id, text });
    }

    if (items.length === 0) {
      this.setAttribute('data-toc-empty', '');
      return;
    }

    for (const nav of this.navs) {
      nav.appendChild(this.#renderList(items));
    }

    this.#syncContainers();
    this.#resizeListener = () => this.#syncContainers();
    window.addEventListener('resize', this.#resizeListener);

    this.#bindClicks();
    this.#observeSections(headings);
    this.#injectStructuredData(items);
  }

  #renderList(items) {
    const list = document.createElement('ol');
    list.className = 'blog-post-toc__list';

    items.forEach((item, index) => {
      const listItem = document.createElement('li');
      listItem.className = 'blog-post-toc__item';

      const link = document.createElement('a');
      link.className = 'blog-post-toc__link';
      link.href = `#${item.id}`;
      link.textContent = item.text;
      link.dataset.tocLink = item.id;
      if (index === 0) link.setAttribute('aria-current', 'true');

      listItem.appendChild(link);
      list.appendChild(listItem);
    });

    return list;
  }

  #bindClicks() {
    this.addEventListener('click', (event) => {
      const link = event.target instanceof Element ? event.target.closest('[data-toc-link]') : null;
      if (!link) return;

      const id = link.dataset.tocLink;
      const target = id ? document.getElementById(id) : null;
      if (!target) return;

      event.preventDefault();
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      history.replaceState(null, '', `#${id}`);

      const mobile = link.closest('[data-toc-mobile]');
      if (mobile instanceof HTMLDetailsElement) mobile.open = false;
    });
  }

  #setActive(id) {
    for (const link of this.querySelectorAll('[data-toc-link]')) {
      if (link.dataset.tocLink === id) {
        link.setAttribute('aria-current', 'true');
      } else {
        link.removeAttribute('aria-current');
      }
    }
  }

  #observeSections(headings) {
    if (!('IntersectionObserver' in window)) return;

    let activeId = headings[0]?.id ?? null;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            activeId = entry.target.id;
          }
        }
        if (activeId) this.#setActive(activeId);
      },
      {
        rootMargin: '-20% 0px -70% 0px',
        threshold: 0,
      }
    );

    for (const heading of headings) {
      this.#observer.observe(heading);
    }
  }

  #injectStructuredData(items) {
    const base = `${location.origin}${location.pathname}`;
    const data = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: this.dataset.tocName || 'Table of contents',
      itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.text,
        url: `${base}#${item.id}`,
      })),
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(data);
    this.appendChild(script);
  }
}

if (!customElements.get('blog-post-toc')) {
  customElements.define('blog-post-toc', BlogPostToc);
}
